import { QueryKey } from '@tanstack/react-query';
import { queryClient } from 'queryClient';
import useAppMutation from 'hooks/useAppMutation';
import { getIntegrationPropertySettingsQueryKey } from 'hooks/useFetchIntegrationPropertySettings';
import useNotify from 'hooks/useNotify';
import {
  IntegrationEnum,
  IntegrationStatus,
  PostOutboundOAuthRedirectURLResponseTO,
  PropertySettingsResponseTO,
} from 'models/InternalIntegrationSetting';
import { useTranslation } from 'react-i18next';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

interface OauthPropertyRedirectParams {
  enumId: IntegrationEnum;
  propertyUid: string;
  queryKey: QueryKey;
}

const useOauthPropertyRedirect = () => {
  const { notifyError } = useNotify();
  const { t } = useTranslation();

  const { mutate: openOauthRedirect, isLoading } = useAppMutation(
    async (params: OauthPropertyRedirectParams) => {
      const { enumId, propertyUid } = params;

      const response = await API.post<PostOutboundOAuthRedirectURLResponseTO>(
        `/api/internal/integrations/outbound/${enumId}/oauth/properties/${propertyUid}`,
      );

      window.location.href = response?.data?.oauthDetails?.redirectUrl;
    },
    {
      onMutate: async (params: OauthPropertyRedirectParams) => {
        const { enumId, propertyUid } = params;

        const { queryKey } = params;

        await queryClient.cancelQueries({
          queryKey,
        });

        // Snapshot the previous value
        const previousData =
          queryClient.getQueryData<PropertySettingsResponseTO>(
            getIntegrationPropertySettingsQueryKey({
              enumId,
              propertyUids: [propertyUid],
            }),
          );

        // Optimistically update to the new value
        queryClient.setQueriesData<PropertySettingsResponseTO>(
          queryKey,
          (cachedData) => {
            if (!cachedData) return cachedData;
            return {
              ...cachedData,
              propertySettings: cachedData.propertySettings.map((item) => {
                if (item.propertyUid !== propertyUid) return item;

                return {
                  ...item,
                  status: IntegrationStatus.ACTIVE,
                };
              }),
            };
          },
        );

        // Return a context object with the snapshotted value
        return { previousData, queryKey };
      },
      onError: (error: any, __, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousData && context?.queryKey) {
          queryClient.setQueryData(context.queryKey, context.previousData);
        }

        notifyError(
          getErrorMessage({
            apiErrorCode: error.response.data?.apiErrorCode,
            apiErrorMessage: error.response.data?.apiErrorMessage,
            baseKey: 'pageIntegrations.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return {
    openOauthRedirect,
    isLoading,
  };
};

export default useOauthPropertyRedirect;
